<!--
  @component

  The tag component

  ```html
  <oc-tag-v1 variant="info-primary" size="100">Label</oc-tag-v1>
  ```

  @slot required `default` content of the label. Should usually only contain text. (Not available if variant=sold-out)
-->
<svelte:options
  customElement={{
    tag: "oc-tag-v1",
    /**
 *
 */
    extend: (CustomElementConstructor) =>
      class extends CustomElementConstructor {
        constructor() {
          super();
          this.host = this;
        }
      },
    props: {
      size: { type: "String", reflect: true },
      variant: { type: "String", reflect: true },
      showInfoIcon: { type: "Boolean", attribute: "show-info-icon" },
    },
  }}
/>

<script lang="ts">
  type TagVariant =
    | "info-primary"
    | "sale"
    | "sold-out"
    | "error"
    | "success"
    | "warning"
    | "hint"
    | "info-secondary";

  /**
 *
 *
 *
 */
  export let size: "25" | "50" | "100" = "100";

  /**
 *
 *
 *
 */
  export let variant: TagVariant = "info-primary";

  /**
 *
 *
 *
 */
  export let showInfoIcon: boolean = false;

  /**
 *
 */
  export let host: HTMLElement;

  $: if (showInfoIcon) {
    if (host.tabIndex === -1) host.tabIndex = 0;
    if (!host.role) host.role = "button";
  }
</script>

<span class={`tag tag--size-${size}`}
  >{#if variant === "sold-out"}Ausverkauft{:else}<span class="tag__label"><slot /></span
    >{/if}{#if showInfoIcon}
    <oc-icon-v1
      class={`tag__icon tag__icon--size-${size}`}
      type="info-hint"
      size="50"
      color="currentColor"
    />{/if}</span
>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";
  @use "./TagV1.config" as config;

  .tag {
    display: inline-flex;
    align-items: center;
    max-width: 100%;

    &--size-25 {
      gap: tokens.$oc-component-tag-25-gap-x;
    }

    &--size-50 {
      gap: tokens.$oc-component-tag-50-gap-x;
    }

    &--size-100 {
      gap: tokens.$oc-component-tag-100-gap-x;
    }

    &__label {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .tag__icon {
    &--size-25 {
      width: tokens.$oc-component-tag-25-icon-size;
      height: tokens.$oc-component-tag-25-icon-size;
    }

    &--size-50 {
      width: tokens.$oc-component-tag-50-icon-size;
      height: tokens.$oc-component-tag-50-icon-size;
    }

    &--size-100 {
      width: tokens.$oc-component-tag-100-icon-size;
      height: tokens.$oc-component-tag-100-icon-size;
    }
  }

  :host {
    @include config.oc-tag;
  }

  :host([size="25"]) {
    @include config.oc-tag-size-25;
  }

  :host([size="50"]) {
    @include config.oc-tag-size-50;
  }

  :host([size="100"]) {
    @include config.oc-tag-size-100;
  }

  /*                                          */

  :host([variant="info-primary"]) {
    background-color: tokens.$oc-component-tag-info-primary-background-color;
    color: tokens.$oc-component-tag-info-primary-text-color;
  }

  :host([variant="sale"]) {
    background-color: tokens.$oc-component-tag-sale-background-color;
    color: tokens.$oc-component-tag-sale-text-color;
  }

  :host([variant="sold-out"]) {
    background-color: tokens.$oc-component-tag-sold-out-background-color;
    color: tokens.$oc-component-tag-sold-out-text-color;
  }

  :host([variant="error"]) {
    background-color: tokens.$oc-component-tag-error-background-color;
    color: tokens.$oc-component-tag-error-text-color;
  }

  :host([variant="success"]) {
    background-color: tokens.$oc-component-tag-success-background-color;
    color: tokens.$oc-component-tag-success-text-color;
  }

  :host([variant="warning"]) {
    background-color: tokens.$oc-component-tag-warning-background-color;
    color: tokens.$oc-component-tag-warning-text-color;
  }

  :host([variant="hint"]) {
    background-color: tokens.$oc-component-tag-hint-background-color;
    color: tokens.$oc-component-tag-hint-text-color;
  }

  :host([variant="info-secondary"]) {
    background-color: tokens.$oc-component-tag-info-secondary-background-color;
    color: tokens.$oc-component-tag-info-secondary-text-color;
  }

  :host {
    border-radius: tokens.$oc-component-tag-border-radius;
  }

  :host(:focus) {
    outline: 2px solid tokens.$oc-semantic-focus-outline-color;
    outline-offset: tokens.$oc-semantic-focus-outline-offset;
  }

  :host([tabindex]) {
    cursor: pointer;
  }
</style>
